import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileDown, Pencil, Trash2, Contact, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, API } from "@/lib/api";

export default function CvList() {
  const navigate = useNavigate();
  const [cvs, setCvs] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadExisting = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/cvs/parse-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`CV imported for ${data.full_name} — review and polish`);
      navigate(`/cvs/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not read that CV");
    } finally {
      setUploading(false);
    }
  };

  const load = () => api.get("/cvs").then((r) => setCvs(r.data)).catch(() => setCvs([]));
  useEffect(() => { load(); }, []);

  const downloadPdf = async (cv) => {
    setBusyId(cv.id);
    try {
      const res = await api.get(`/cvs/${cv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CV-${(cv.full_name || "cv").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (cv) => {
    if (!window.confirm(`Delete the CV for ${cv.full_name || "this person"}?`)) return;
    await api.delete(`/cvs/${cv.id}`);
    toast.success("CV deleted");
    load();
  };

  return (
    <Layout hideFooter>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-3xl text-gray-900">Tender CVs</h1>
            <p className="text-sm text-gray-600 mt-1">On-brand staff CVs, ready to attach to bids and frameworks.</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
              onChange={uploadExisting} data-testid="cv-upload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="rcw-btn-ghost" data-testid="cv-upload-existing-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Reading CV…" : "Upload existing"}
            </button>
            <button onClick={() => navigate("/cvs/new")} className="rcw-btn-amber" data-testid="cv-new-btn">
              <Plus className="h-4 w-4" /> New CV
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {cvs === null && <div className="text-sm text-gray-500">Loading…</div>}
          {cvs !== null && cvs.length === 0 && (
            <div className="rcw-card p-8 text-center" data-testid="cv-empty">
              <Contact className="h-8 w-8 text-sky-500 mx-auto" />
              <div className="mt-3 font-display font-semibold text-lg">No CVs yet</div>
              <p className="text-sm text-gray-600 mt-1">Create the first tender CV — it takes about five minutes with AI polishing the wording.</p>
            </div>
          )}
          {(cvs || []).map((cv) => (
            <div key={cv.id} className="rcw-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`cv-row-${cv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-gray-900">{cv.full_name || "Untitled CV"}</div>
                <div className="text-sm text-gray-600">
                  {cv.job_title || "—"}{cv.tender_name ? ` · ${cv.tender_name}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(cv)} className="rcw-btn-ghost text-sm" data-testid={`cv-pdf-${cv.id}`}>
                  {busyId === cv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
                </button>
                <button onClick={() => navigate(`/cvs/${cv.id}`)} className="rcw-btn-ghost text-sm" data-testid={`cv-edit-${cv.id}`}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
                <button onClick={() => remove(cv)} className="rcw-btn-ghost text-sm text-red-600" data-testid={`cv-delete-${cv.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
